import { ArrowLeft, FileText, CheckCircle, AlertTriangle, CreditCard, Scale, RefreshCw, Mail } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function Terms() {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen bg-white dark:bg-[#1D1D1F]">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 dark:bg-[#1D1D1F]/95 backdrop-blur-xl border-b border-black/5 dark:border-white/10">
        <div className="max-w-3xl mx-auto px-4 h-14 flex items-center gap-3">
          <button 
            onClick={() => navigate(-1)}
            className="w-10 h-10 rounded-xl bg-[#F5F5F7] dark:bg-white/10 flex items-center justify-center"
          >
            <ArrowLeft size={20} className="text-[#1D1D1F] dark:text-white" />
          </button>
          <h1 className="text-lg font-bold text-[#1D1D1F] dark:text-white">Termini di Servizio</h1>
        </div>
      </header>
      
      <main className="max-w-3xl mx-auto px-4 py-8">
        {/* Hero */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-[#FF6B4A] to-[#FF8F6B] flex items-center justify-center">
            <FileText className="text-white" size={32} />
          </div>
          <h1 className="text-2xl font-bold text-[#1D1D1F] dark:text-white mb-2">
            Termini di Servizio
          </h1>
          <p className="text-[#6E6E73] dark:text-gray-400">
            Ultimo aggiornamento: Marzo 2024
          </p>
        </div>
        
        {/* Content */}
        <div className="space-y-8 text-[#1D1D1F] dark:text-gray-200">
          {/* Intro */}
          <section>
            <p className="text-[#6E6E73] dark:text-gray-400 leading-relaxed">
              Benvenuto su Zampa. Utilizzando il nostro servizio, accetti i seguenti termini e condizioni. 
              Ti preghiamo di leggerli attentamente.
            </p>
          </section>
          
          {/* Section 1 */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-blue-100 dark:bg-blue-500/20 flex items-center justify-center">
                <CheckCircle className="text-blue-600 dark:text-blue-400" size={20} />
              </div>
              <h2 className="text-lg font-bold">1. Descrizione del Servizio</h2>
            </div>
            <div className="pl-13 text-[#6E6E73] dark:text-gray-400 space-y-3">
              <p>
                Zampa è il <strong className="text-[#1D1D1F] dark:text-white">fascicolo digitale ufficiale del cane</strong>, 
                una piattaforma che permette di:
              </p>
              <ul className="list-disc list-inside ml-4 space-y-1">
                <li>Creare e gestire il profilo completo del tuo animale</li>
                <li>Archiviare documentazione sanitaria (vaccini, referti, prescrizioni)</li>
                <li>Condividere i dati con veterinari e autorità tramite QR code</li>
                <li>Ricevere promemoria per scadenze sanitarie</li>
              </ul>
            </div>
          </section>
          
          {/* Section 2 */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 dark:bg-emerald-500/20 flex items-center justify-center">
                <Scale className="text-emerald-600 dark:text-emerald-400" size={20} />
              </div>
              <h2 className="text-lg font-bold">2. Obblighi dell'Utente</h2>
            </div>
            <ul className="pl-13 space-y-2 text-[#6E6E73] dark:text-gray-400 list-disc list-inside">
              <li>Fornire informazioni accurate e veritiere</li>
              <li>Mantenere la sicurezza delle credenziali di accesso</li>
              <li>Non utilizzare il servizio per scopi illegali o fraudolenti</li>
              <li>Non caricare contenuti offensivi, illegali o che violino diritti di terzi</li>
              <li>Essere maggiorenne o avere il consenso di un tutore legale</li>
            </ul>
          </section>
          
          {/* Section 3 */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-amber-100 dark:bg-amber-500/20 flex items-center justify-center">
                <AlertTriangle className="text-amber-600 dark:text-amber-400" size={20} />
              </div>
              <h2 className="text-lg font-bold">3. Limitazioni di Responsabilità</h2>
            </div>
            <div className="pl-13 text-[#6E6E73] dark:text-gray-400 space-y-3">
              <p>
                <strong className="text-[#1D1D1F] dark:text-white">Zampa non è un servizio medico veterinario.</strong> 
                Le informazioni presenti nell'app sono fornite dall'utente e non sostituiscono il parere di un veterinario.
              </p>
              <p>Non siamo responsabili per:</p>
              <ul className="list-disc list-inside ml-4 space-y-1">
                <li>Decisioni mediche basate sui dati inseriti nell'app</li>
                <li>Perdita di dati dovuta a cause di forza maggiore</li>
                <li>Interruzioni temporanee del servizio per manutenzione</li>
                <li>Uso improprio del servizio da parte dell'utente</li>
              </ul>
            </div>
          </section>
          
          {/* Section 4 */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-purple-100 dark:bg-purple-500/20 flex items-center justify-center">
                <CreditCard className="text-purple-600 dark:text-purple-400" size={20} />
              </div>
              <h2 className="text-lg font-bold">4. Servizi a Pagamento</h2>
            </div>
            <div className="pl-13 text-[#6E6E73] dark:text-gray-400 space-y-3">
              <p>Zampa offre funzionalità base gratuite. Alcuni servizi premium potrebbero richiedere un pagamento.</p>
              <ul className="list-disc list-inside ml-4 space-y-1">
                <li>I prezzi sono indicati in Euro e includono IVA dove applicabile</li>
                <li>I pagamenti sono gestiti tramite provider sicuri (es. Stripe)</li>
                <li>Gli abbonamenti si rinnovano automaticamente salvo disdetta</li>
                <li>Il rimborso è possibile entro 14 giorni dall'acquisto, secondo la normativa vigente</li>
              </ul>
            </div>
          </section>
          
          {/* Section 5 */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-red-100 dark:bg-red-500/20 flex items-center justify-center">
                <RefreshCw className="text-red-600 dark:text-red-400" size={20} />
              </div>
              <h2 className="text-lg font-bold">5. Modifica e Cessazione</h2>
            </div>
            <div className="pl-13 text-[#6E6E73] dark:text-gray-400 space-y-3">
              <p>Ci riserviamo il diritto di:</p>
              <ul className="list-disc list-inside ml-4 space-y-1">
                <li>Modificare questi termini con preavviso di 30 giorni</li>
                <li>Sospendere o terminare account che violano i termini</li>
                <li>Modificare le funzionalità del servizio</li>
              </ul>
              <p>Puoi cancellare il tuo account in qualsiasi momento dalle Impostazioni. I tuoi dati saranno eliminati entro 30 giorni.</p>
            </div>
          </section>
          
          {/* Section 6 */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-[#FF6B4A]/10 flex items-center justify-center">
                <Mail className="text-[#FF6B4A]" size={20} />
              </div>
              <h2 className="text-lg font-bold">6. Legge Applicabile e Contatti</h2>
            </div>
            <div className="pl-13 text-[#6E6E73] dark:text-gray-400 space-y-3">
              <p>
                Questi termini sono regolati dalla legge italiana. Per qualsiasi controversia, 
                sarà competente il Foro di Milano.
              </p>
              <p className="mt-2">
                <strong className="text-[#1D1D1F] dark:text-white">Contatti:</strong>{" "}
                <a href="mailto:supporto@zampa.one" className="text-[#FF6B4A] underline">supporto@zampa.one</a>
              </p>
            </div>
          </section>
        </div>
        
        {/* Footer note */}
        <div className="mt-12 p-4 bg-[#F5F5F7] dark:bg-white/5 rounded-2xl">
          <p className="text-sm text-[#86868B] dark:text-gray-500 text-center">
            Utilizzando Zampa, confermi di aver letto e accettato questi termini.
          </p>
        </div>
      </main>
    </div>
  );
}
